/**
 * Design-Tokens (docs/11 + docs/12) — EINE Quelle fuer CSS und Canvas.
 * Farbe-zu-Keyword ist verbindliche Spielregel (docs/12 §2), kein Flavor.
 */

export const TEAM_COLORS = { A: '#4da3ff', B: '#ff5d5d' } as const

/** Seltenheitsfarben (docs/11 UI Style: "klare Seltenheitsfarben") */
export const RARITY_COLORS: Record<string, string> = {
  common: '#a89a82',
  rare: '#4aa3ff',
  epic: '#b06ef0',
}

/** verbindliche Keyword-/Elementfarben (docs/12 §2) */
export const KEYWORD_COLORS = {
  poison: '#7ed321',
  burn: '#ff7a29',
  ice: '#5bd8f0',
  holy: '#ffd94a',
  shadow: '#9a6dd7',
  tech: '#9fc9c9',
  shield: '#4aa3ff',
  physical: '#f0e6d2',
  crit: '#ffd94a',
} as const

/** Ability -> Elementfarbe (Cast-Ring, docs/12 §2) */
export const ABILITY_COLORS: Record<string, string> = {
  bulwark: KEYWORD_COLORS.shield,
  shadowstrike: KEYWORD_COLORS.shadow,
  piercingShot: KEYWORD_COLORS.tech,
  healingWave: KEYWORD_COLORS.holy,
  plagueCloud: KEYWORD_COLORS.poison,
  // Content-Batch 1 (BATTLE-9)
  aegis: KEYWORD_COLORS.shield,
  fireball: KEYWORD_COLORS.burn,
  chaosbolt: KEYWORD_COLORS.shadow,
  moonfire: KEYWORD_COLORS.burn,
  deathHarvest: KEYWORD_COLORS.shadow,
  flurry: KEYWORD_COLORS.physical,
  felNova: KEYWORD_COLORS.burn,
  chainLightning: KEYWORD_COLORS.ice,
  boneSpear: KEYWORD_COLORS.shadow,
  bombToss: KEYWORD_COLORS.tech,
}

/** Spezies-Grundtoene pro Champion (docs/12 §1, Prototyp-Mapping) */
export interface SpeciesColors {
  body: string
  bodyDark: string // Schattierung/Ridges
  accent: string // Blattgruen/Kelch/Huelle
  outline: string
}

export const SPECIES: Record<string, SpeciesColors> = {
  // Iron Guard -> Krieger -> Kohlrabi (HELL-gelbgruen, robuste Stummel) — Hue-Spreizung ggue. Okra (F4)
  ironguard: { body: '#bcd07a', bodyDark: '#94ab52', accent: '#6d8f3e', outline: '#28311a' },
  // Rogue -> Schurke -> Aubergine (dunkelviolett, Kelch als Kapuze)
  rogue: { body: '#5e3070', bodyDark: '#432152', accent: '#3f5f37', outline: '#1c0f22' },
  // Musketeer -> Jaeger -> Karotte (orange, Blattgruen als Befiederung)
  musketeer: { body: '#e8842e', bodyDark: '#b95f1c', accent: '#4e8f3a', outline: '#3a2008' },
  // Cleric -> Priester -> Knoblauch (Papierhuelle als Robe)
  cleric: { body: '#ece2ce', bodyDark: '#c9b99c', accent: '#b3a284', outline: '#3a3122' },
  // Hexmaster -> Hexendoktor -> Okra (DUNKEL-teal-gruen, gerippt) — Hue-Spreizung ggue. Kohlrabi (F4)
  hexmaster: { body: '#3f8a63', bodyDark: '#2c6647', accent: '#7bd24a', outline: '#122a1e' },
  // ===== Content-Batch 1 (BATTLE-9, docs/12 §1) =====
  // Paladin -> Ananas (goldgelb, Rautenmuster, gruene Krone)
  paladin: { body: '#d9a441', bodyDark: '#ab7c26', accent: '#4e8f3a', outline: '#3a2a0a' },
  // Magier -> Rote Beete (magenta-karmin, Blattstiele)
  mage: { body: '#a53860', bodyDark: '#7c2545', accent: '#5f8f3e', outline: '#2c0d18' },
  // Hexenmeister -> Brombeere (fast-schwarzes Violett, Beeren-Noppen)
  warlock: { body: '#3d2b4f', bodyDark: '#291b37', accent: '#9a6dd7', outline: '#140d1c' },
  // Druide -> Kuerbis (sattes Orange, Rippen, gruener Stiel)
  druid: { body: '#d67229', bodyDark: '#a75318', accent: '#5f8f3e', outline: '#38200a' },
  // Todesritter -> Pastinake (blasses Knochenbeige, Frost-Akzent)
  deathknight: { body: '#d8cfb4', bodyDark: '#b0a686', accent: '#5bd8f0', outline: '#332f22' },
  // Moench -> Birne (hell-gelbgruen, sanft; Hue naeher gelb als Kohlrabi)
  monk: { body: '#c3cf6f', bodyDark: '#9aa64a', accent: '#7a5b40', outline: '#2e3116' },
  // Daemonenjaeger -> Peperoni (Chili-Rot, glaenzend)
  demonhunter: { body: '#c8342c', bodyDark: '#96201b', accent: '#4e8f3a', outline: '#320c09' },
  // Schamane -> Kokosnuss (Faser-Braun, helles Fruchtfleisch-Akzent)
  shaman: { body: '#7a5b40', bodyDark: '#59402b', accent: '#ece2ce', outline: '#241a10' },
  // Nekromant -> Schwarzer Rettich (Fast-Schwarz mit Violett-Schimmer, weisses Fleisch)
  necromancer: { body: '#2e2a33', bodyDark: '#1d1a21', accent: '#9a6dd7', outline: '#0e0c11' },
  // Tueftler -> Walnuss (Nussbraun, gefurcht, Kupfer-Tech-Akzent)
  tinker: { body: '#97713f', bodyDark: '#6f5029', accent: '#c98a4b', outline: '#2a1e0e' },
}

export const SPECIES_FALLBACK: SpeciesColors = {
  body: '#c8975a', bodyDark: '#9d743e', accent: '#6e8f4a', outline: '#2e2213',
}

/** Spezies-Emoji fuer UI-Listen (Bench, Roster, Shop) */
export const SPECIES_EMOJI: Record<string, string> = {
  ironguard: '🥬',
  rogue: '🍆',
  musketeer: '🥕',
  cleric: '🧄',
  hexmaster: '🫛',
  // Content-Batch 1 (BATTLE-9)
  paladin: '🍍',
  mage: '🍠',
  warlock: '🫐',
  druid: '🎃',
  deathknight: '🥕',
  monk: '🍐',
  demonhunter: '🌶️',
  shaman: '🥥',
  necromancer: '🖤',
  tinker: '🌰',
}

/** Board-Toene (docs/11 Board Style: warm, sauber, hebt Charaktere hervor) */
export const BOARD = {
  bg: '#171310',
  hexTones: ['#495836', '#4f5e39', '#455132'], // deterministische Grasvariation (aufgehellt fuer Arena-Boden, BATTLE-17)
  hexBorder: 'rgba(20, 16, 10, 0.55)',
  stone: '#57493a',
  centerLine: 'rgba(232, 179, 61, 0.35)',
  fractureFill: '#241a12',
  fractureCrack: '#ff6a30',
} as const
